import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Loader2, ShoppingBag, Save } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { RcButton } from "@/components/RcButton";
import { useSeo } from "@/lib/useSeo";
import {
  NECK_LABELS, SLEEVE_LABELS, LENGTH_LABELS, COLOR_SWATCHES, formatINR,
} from "@/lib/options";

const Section = ({ label, children }) => (
  <div className="border-t border-[#362B28] pt-8">
    <div className="text-xs tracking-[0.3em] uppercase text-[#A39E93] mb-5">{label}</div>
    {children}
  </div>
);

const OptionTile = ({ active, onClick, children, testid, style }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    style={style}
    className={`px-4 py-3 text-sm tracking-wide text-left border transition-all rounded-sm ${
      active
        ? "border-[#CFA05E] bg-[#CFA05E]/10 text-[#FDFBF7] ring-1 ring-[#CFA05E]"
        : "border-[#362B28] bg-[#120D0C] text-[#A39E93] hover:border-[#CFA05E]/60 hover:text-[#FDFBF7]"
    }`}
  >
    {children}
  </button>
);

export default function Designer() {
  useSeo({
    title: "Design Your Own Dress — Fabric, Neckline, Embroidery | Park Collections",
    description: "Create a bespoke kurti, lehenga, anarkali, gown or dress. Pick fabric (silk, velvet, georgette), neckline, sleeves, embroidery and enter your measurements. AI preview before you order.",
    path: "/designer",
  });
  const { user } = useAuth();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);

  const [name, setName] = useState("My Bespoke Design");
  const [dressType, setDressType] = useState("kurti");
  const [fabric, setFabric] = useState("silk");
  const [color, setColor] = useState(COLOR_SWATCHES[0].hex);
  const [neck, setNeck] = useState("round");
  const [sleeve, setSleeve] = useState("full");
  const [length, setLength] = useState("midi");
  const [embroidery, setEmbroidery] = useState("plain");
  const [lining, setLining] = useState(false);
  const [m, setM] = useState({});

  const [savedId, setSavedId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [sizePreset, setSizePreset] = useState(null);

  useEffect(() => {
    api.get("/catalog").then((r) => setCatalog(r.data)).catch(() => {});
  }, []);

  const applySize = (key) => {
    if (!catalog?.size_presets?.[key]) return;
    setSizePreset(key);
    setM({ ...catalog.size_presets[key].measurements, notes: m.notes, size_preset: key });
  };

  const measurementsValid = m.bust > 0 && m.waist > 0 && m.hip > 0;

  const price = useMemo(() => {
    if (!catalog) return 0;
    const dt = catalog.dress_types[dressType];
    const fab = catalog.fabrics[fabric];
    const emb = catalog.embroidery[embroidery];
    if (!dt || !fab || !emb) return 0;
    let total = dt.base * fab.multiplier + emb.price + (lining ? catalog.lining_price : 0);
    return Math.round(total);
  }, [catalog, dressType, fabric, embroidery, lining]);

  const ethnic = useMemo(() => {
    if (!catalog) return [];
    return Object.entries(catalog.dress_types).filter(([, v]) => v.category === "ethnic");
  }, [catalog]);
  const western = useMemo(() => {
    if (!catalog) return [];
    return Object.entries(catalog.dress_types).filter(([, v]) => v.category === "western");
  }, [catalog]);

  const guardLogin = () => {
    if (!user) {
      navigate("/auth", { state: { from: "/designer" } });
      return false;
    }
    return true;
  };

  const guardMeasurements = () => {
    if (!measurementsValid) {
      toast.error("Please enter or pick a size — bust, waist and hip are required.");
      const el = document.getElementById("measurements-section");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      return false;
    }
    return true;
  };

  const buildPayload = () => ({
    name, dress_type: dressType, fabric, color, neck, sleeve, length,
    embroidery, lining, measurements: m,
  });

  const ensureSaved = async () => {
    if (savedId) return savedId;
    const { data } = await api.post("/designs", buildPayload());
    setSavedId(data.id);
    return data.id;
  };

  const handleSave = async () => {
    if (!guardLogin()) return;
    if (!guardMeasurements()) return;
    setSaving(true);
    try {
      const id = await ensureSaved();
      toast.success("Design saved.");
      return id;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const handlePreview = async () => {
    if (!guardLogin()) return;
    if (!guardMeasurements()) return;
    setGenerating(true);
    try {
      const id = await ensureSaved();
      const { data } = await api.post(`/designs/${id}/preview`);
      setPreview(data.preview_image);
      toast.success("AI preview ready.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setGenerating(false); }
  };

  const handleCheckout = async () => {
    if (!guardLogin()) return;
    if (!guardMeasurements()) return;
    setCheckingOut(true);
    try {
      const id = await ensureSaved();
      const origin = window.location.origin;
      const { data } = await api.post("/checkout/session", { design_id: id, origin_url: origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setCheckingOut(false);
    }
  };

  if (!catalog) {
    return <div className="p-16 text-center text-[#A39E93]">Loading the atelier…</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 grid lg:grid-cols-12 gap-8">
      {/* LEFT: Preview & summary (sticky) */}
      <aside className="lg:col-span-5 lg:sticky lg:top-24 self-start space-y-6">
        <div className="aspect-[3/4] relative bg-[#1F1816] border border-[#362B28] overflow-hidden rounded-sm">
          {preview ? (
            <img src={preview} alt="AI preview" className="w-full h-full object-cover" data-testid="preview-image" />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-[#A39E93] p-8 text-center">
              <Sparkles className="w-8 h-8 text-[#CFA05E] mb-4" strokeWidth={1.2} />
              <div className="font-display text-2xl mb-2 text-[#FDFBF7]">Awaiting your design</div>
              <div className="text-sm font-light max-w-xs">
                Pick your selections, then generate an AI preview to see your garment come to life.
              </div>
            </div>
          )}
          {generating && (
            <div className="absolute inset-0 bg-[#120D0C]/85 flex flex-col items-center justify-center gap-3 backdrop-blur">
              <Loader2 className="w-8 h-8 text-[#CFA05E] animate-spin" strokeWidth={1.4} />
              <div className="text-sm tracking-wider text-[#A39E93]">Stitching your preview…</div>
            </div>
          )}
        </div>

        <div className="bg-[#1F1816] border border-[#362B28] rounded-sm p-6">
          <div className="flex items-baseline justify-between mb-4">
            <div className="text-xs tracking-[0.3em] uppercase text-[#A39E93]">Estimated</div>
            <div className="font-display text-4xl text-[#CFA05E]" data-testid="live-price">{formatINR(price)}</div>
          </div>
          <div className="text-[11px] text-[#A39E93] mb-5 leading-relaxed">
            Includes fabric, craftsmanship and finishing. Tailoring done to your measurements.
          </div>

          <div className="grid grid-cols-2 gap-3">
            <RcButton variant="outline" onClick={handleSave} disabled={saving} data-testid="save-design-btn">
              <Save className="w-4 h-4" strokeWidth={1.4} /> {saving ? "Saving…" : "Save"}
            </RcButton>

            <div className="beam-border rounded-sm">
              <button
                onClick={handlePreview}
                disabled={generating}
                data-testid="generate-preview-btn"
                className="w-full px-6 py-3 text-sm tracking-wider uppercase font-medium bg-[#1F1816] text-[#CFA05E] hover:bg-[#2A201D] transition rounded-sm inline-flex items-center justify-center gap-2"
              >
                <Sparkles className="w-4 h-4" strokeWidth={1.4} /> {generating ? "Generating…" : "AI Preview"}
              </button>
            </div>
          </div>

          <RcButton onClick={handleCheckout} disabled={checkingOut} className="w-full mt-3" data-testid="checkout-btn">
            <ShoppingBag className="w-4 h-4" strokeWidth={1.4} /> {checkingOut ? "Redirecting…" : `Order — ${formatINR(price)}`}
          </RcButton>
        </div>
      </aside>

      {/* RIGHT: Configurator */}
      <div className="lg:col-span-7 space-y-10">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-[#CFA05E] mb-2">Configurator</div>
          <h1 className="font-display text-5xl tracking-tight">Compose your garment.</h1>
        </div>

        <Section label="Name your design">
          <input
            data-testid="design-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-[#120D0C] border border-[#362B28] focus:border-[#CFA05E] focus:ring-1 focus:ring-[#CFA05E] outline-none px-4 py-3 text-sm transition"
          />
        </Section>

        <Section label="Dress Type · Indian Ethnic">
          <div className="grid sm:grid-cols-3 gap-3">
            {ethnic.map(([k, v]) => (
              <OptionTile key={k} active={dressType === k} onClick={() => setDressType(k)} testid={`dress-${k}`}>
                <div className="font-display text-lg">{v.label}</div>
                <div className="text-xs text-[#A39E93]">from {formatINR(v.base)}</div>
              </OptionTile>
            ))}
          </div>
        </Section>

        <Section label="Dress Type · Western">
          <div className="grid sm:grid-cols-4 gap-3">
            {western.map(([k, v]) => (
              <OptionTile key={k} active={dressType === k} onClick={() => setDressType(k)} testid={`dress-${k}`}>
                <div className="font-display text-lg">{v.label}</div>
                <div className="text-xs text-[#A39E93]">from {formatINR(v.base)}</div>
              </OptionTile>
            ))}
          </div>
        </Section>

        <Section label="Fabric">
          <div className="grid sm:grid-cols-3 gap-3">
            {Object.entries(catalog.fabrics).map(([k, v]) => (
              <OptionTile key={k} active={fabric === k} onClick={() => setFabric(k)} testid={`fabric-${k}`}>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-sm border border-[#362B28]" style={{ background: v.swatch }} />
                  <div>
                    <div className="font-display text-lg leading-none">{v.label}</div>
                    <div className="text-xs text-[#A39E93]">×{v.multiplier.toFixed(1)}</div>
                  </div>
                </div>
              </OptionTile>
            ))}
          </div>
        </Section>

        <Section label="Color">
          <div className="grid grid-cols-6 sm:grid-cols-12 gap-2">
            {COLOR_SWATCHES.map((c) => (
              <button
                key={c.hex}
                type="button"
                onClick={() => setColor(c.hex)}
                data-testid={`color-${c.hex.replace("#", "")}`}
                title={c.name}
                style={{ background: c.hex }}
                className={`aspect-square rounded-sm border transition ${
                  color === c.hex ? "ring-2 ring-[#CFA05E] border-transparent scale-110" : "border-[#362B28] hover:border-[#CFA05E]/60"
                }`}
              />
            ))}
            <label className="aspect-square rounded-sm border border-[#362B28] flex items-center justify-center cursor-pointer hover:border-[#CFA05E]/60" title="Custom color">
              <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="opacity-0 w-0 h-0" data-testid="color-custom" />
              <span className="text-[10px] tracking-wider text-[#A39E93]">CUSTOM</span>
            </label>
          </div>
        </Section>

        <Section label="Neckline">
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {catalog.necks.map((k) => (
              <OptionTile key={k} active={neck === k} onClick={() => setNeck(k)} testid={`neck-${k}`}>
                {NECK_LABELS[k]}
              </OptionTile>
            ))}
          </div>
        </Section>

        <Section label="Sleeves">
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {catalog.sleeves.map((k) => (
              <OptionTile key={k} active={sleeve === k} onClick={() => setSleeve(k)} testid={`sleeve-${k}`}>
                {SLEEVE_LABELS[k]}
              </OptionTile>
            ))}
          </div>
        </Section>

        <Section label="Length">
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
            {catalog.lengths.map((k) => (
              <OptionTile key={k} active={length === k} onClick={() => setLength(k)} testid={`length-${k}`}>
                {LENGTH_LABELS[k]}
              </OptionTile>
            ))}
          </div>
        </Section>

        <Section label="Embellishment">
          <div className="grid sm:grid-cols-3 gap-3">
            {Object.entries(catalog.embroidery).map(([k, v]) => (
              <OptionTile key={k} active={embroidery === k} onClick={() => setEmbroidery(k)} testid={`embroidery-${k}`}>
                <div className="font-display text-base">{v.label}</div>
                <div className="text-xs text-[#A39E93]">{v.price > 0 ? `+${formatINR(v.price)}` : "Included"}</div>
              </OptionTile>
            ))}
          </div>
        </Section>

        <Section label="Lining">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => setLining(!lining)}
              data-testid="lining-toggle"
              className={`relative w-14 h-7 rounded-full border transition ${
                lining ? "bg-[#CFA05E]/20 border-[#CFA05E]" : "bg-[#120D0C] border-[#362B28]"
              }`}
            >
              <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full transition-transform ${
                lining ? "translate-x-7 bg-[#CFA05E]" : "translate-x-0 bg-[#A39E93]"
              }`} />
            </button>
            <div className="text-sm">
              <div className="text-[#FDFBF7]">{lining ? "Lined" : "Unlined"}</div>
              <div className="text-xs text-[#A39E93]">{lining ? `+${formatINR(catalog.lining_price)}` : "No additional cost"}</div>
            </div>
          </div>
        </Section>

        <Section label="Measurements · Required">
          <div id="measurements-section" />
          <div className="text-sm text-[#A39E93] mb-5 font-light">
            Pick a standard size to auto-fill measurements, or enter your own. Bust, waist and hip are mandatory.
          </div>

          <div className="flex flex-wrap gap-2 mb-6">
            {catalog.size_presets && Object.entries(catalog.size_presets).map(([k, v]) => (
              <button
                key={k}
                type="button"
                onClick={() => applySize(k)}
                data-testid={`size-preset-${k}`}
                className={`px-5 py-2 text-xs tracking-[0.2em] uppercase border rounded-sm transition ${
                  sizePreset === k
                    ? "border-[#CFA05E] bg-[#CFA05E]/10 text-[#CFA05E] ring-1 ring-[#CFA05E]"
                    : "border-[#362B28] text-[#A39E93] hover:border-[#CFA05E]/60 hover:text-[#FDFBF7]"
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            {[
              {f:"bust", req:true},{f:"waist", req:true},{f:"hip", req:true},
              {f:"shoulder"},{f:"sleeve_length"},{f:"dress_length"},
            ].map(({f, req}) => (
              <div key={f}>
                <label className="text-xs tracking-[0.2em] uppercase text-[#A39E93] block mb-2">
                  {f.replace("_", " ")} {req && <span className="text-[#CFA05E]">*</span>}
                </label>
                <input
                  type="number"
                  step="0.5"
                  required={req}
                  data-testid={`measure-${f}`}
                  value={m[f] ?? ""}
                  onChange={(e) => { setSizePreset(null); setM({ ...m, [f]: parseFloat(e.target.value) || undefined, size_preset: null }); }}
                  className={`w-full bg-[#120D0C] border focus:border-[#CFA05E] focus:ring-1 focus:ring-[#CFA05E] outline-none px-4 py-3 text-sm transition ${
                    req && !m[f] ? "border-[#6B2737]/60" : "border-[#362B28]"
                  }`}
                />
              </div>
            ))}
            <div className="sm:col-span-3">
              <label className="text-xs tracking-[0.2em] uppercase text-[#A39E93] block mb-2">Notes for tailor</label>
              <textarea
                rows={3}
                data-testid="measure-notes"
                value={m.notes || ""}
                onChange={(e) => setM({ ...m, notes: e.target.value })}
                className="w-full bg-[#120D0C] border border-[#362B28] focus:border-[#CFA05E] focus:ring-1 focus:ring-[#CFA05E] outline-none px-4 py-3 text-sm transition"
              />
            </div>
          </div>

          {!measurementsValid && (
            <div className="mt-4 text-xs text-[#6B2737] tracking-wider" data-testid="measurements-warning">
              Bust, waist and hip are required to save, preview or order.
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}
